// Client-side API functions for calendar sync (Google, ICS, CalDAV).

import type { SyncResult } from "./types";

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest",
    ...(options?.headers as Record<string, string>),
  };

  if (options?.body && typeof options.body === "string") {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: "same-origin",
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error ?? res.statusText);
  }

  return res.json();
}

// --- Google OAuth ---

export interface GoogleSyncStatus {
  configured: boolean;
  connected: boolean;
  lastSyncedAt?: string | null;
  error?: string | null;
  calendars?: Array<{
    googleCalendarId: string;
    localCalendarId: string;
    summary: string;
    color: string | null;
    lastSyncedAt: string | null;
  }>;
}

export async function getGoogleStatus(): Promise<GoogleSyncStatus> {
  return apiFetch("/api/google/status");
}

export async function getGoogleConnectUrl(): Promise<{ url: string }> {
  return apiFetch("/api/google/connect");
}

export async function disconnectGoogle(): Promise<void> {
  await apiFetch("/api/google/disconnect", { method: "POST" });
}

export async function triggerGoogleSync(): Promise<{ results: SyncResult[] }> {
  return apiFetch("/api/google/sync", { method: "POST" });
}

// --- Unified Sync Sources ---

export interface SyncSourceInfo {
  id: string;
  type: "google" | "ics" | "caldav";
  externalCalendarId: string;
  localCalendarId: string;
  lastSyncedAt: string | null;
  calendarSummary: string | null;
  calendarColor: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
}

export async function getSyncSources(): Promise<{ sources: SyncSourceInfo[] }> {
  return apiFetch("/api/sync/sources");
}

export async function removeSyncSource(id: string): Promise<void> {
  await apiFetch(`/api/sync/sources/${id}`, { method: "DELETE" });
}

export async function triggerAllSync(): Promise<{ results: unknown[] }> {
  return apiFetch("/api/sync/trigger", { method: "POST" });
}

// --- ICS ---

export async function addIcsSource(url: string, name?: string): Promise<{
  ok: boolean;
  sourceId: string;
  localCalendarId: string;
  calendarName: string;
}> {
  return apiFetch("/api/sync/ics", {
    method: "POST",
    body: JSON.stringify({ url, name }),
  });
}

// --- CalDAV ---

export async function addCalDavSource(url: string, username: string, password: string): Promise<{
  ok: boolean;
  calendars: Array<{ sourceId: string; localCalendarId: string; name: string }>;
}> {
  return apiFetch("/api/sync/caldav", {
    method: "POST",
    body: JSON.stringify({ url, username, password }),
  });
}
